import DOMPurify from 'isomorphic-dompurify';

/** Tags the journal editor may persist and render. No attributes, no scripts. */
export const JOURNAL_HTML_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'h1',
  'h2',
  'ul',
  'ol',
  'li',
  'blockquote',
  'small',
] as const;

export type JournalTipTapNode = {
  type: string;
  attrs?: { level?: number } | null;
  text?: string;
  content?: JournalTipTapNode[];
};

const BLOCK_TAGS = new Set(['p', 'h1', 'h2', 'ul', 'ol', 'li', 'div', 'blockquote', 'pre']);

export function sanitizeJournalHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...JOURNAL_HTML_ALLOWED_TAGS],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'],
    FORBID_ATTR: ['style', 'src', 'href', 'srcset', 'xlink:href'],
  }).trim();
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Turn stored plain text (and old markdown-ish notes) into editor HTML.
 * A single newline stays a hard break. Each blank line becomes its own empty
 * paragraph — `split(/\n{2,}/)` would collapse those lines into one paragraph
 * break and the template would render pressed together.
 */
export function plainTextToEditorHtml(text: string | null | undefined): string {
  const raw = (text ?? '').replace(/\r\n/g, '\n');
  if (!raw.trim()) return '';
  const lines = raw.split('\n');
  // A final newline ends the last line; it is not an extra blank line.
  if (raw.endsWith('\n')) lines.pop();

  const html: string[] = [];
  let inline: string[] = [];
  const flushInline = () => {
    if (inline.length === 0) return;
    html.push(`<p>${inline.join('<br>')}</p>`);
    inline = [];
  };

  for (const line of lines) {
    if (line.trim().length === 0) {
      flushInline();
      // An empty paragraph is one blank line. A <br> inside it is a second
      // line, because the editor also draws a trailing break.
      html.push('<p></p>');
      continue;
    }
    inline.push(escapeHtml(line));
  }
  flushInline();
  return html.join('');
}

export function journalHtmlForEditor(
  html: string | null | undefined,
  text: string | null | undefined,
): string {
  const sanitized = sanitizeJournalHtml(html);
  if (sanitized) return sanitized;
  return plainTextToEditorHtml(text);
}

function collapseBlankLines(text: string): string {
  // Trim surrounding whitespace and trailing spaces on a line, but keep
  // intentional blank lines. Collapsing `\n{3,}` dropped template spacing.
  return text.replace(/[ \t]+\n/g, '\n').trim();
}

/** One stored paragraph, including an empty one used as vertical spacing. */
function paragraphPlain(inner: string): string {
  if (inner.trim().length === 0) {
    const breaks = inner.match(/\n/g)?.length ?? 0;
    return '\n'.repeat(Math.max(breaks, 1));
  }
  return `${inner}\n`;
}

function inlineText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') return '\n';
  let out = '';
  for (const child of Array.from(el.childNodes)) {
    out += inlineText(child);
  }
  return out;
}

function htmlNodeToPlain(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case 'br':
      return '\n';
    case 'h1':
    case 'h2':
      return `${inlineText(el).trim()}\n`;
    case 'p':
      return paragraphPlain(inlineText(el));
    case 'ul':
    case 'ol': {
      let out = '';
      for (const child of Array.from(el.children)) {
        if (child.tagName.toLowerCase() !== 'li') continue;
        const nested = Array.from(child.children).filter((c) =>
          ['ul', 'ol'].includes(c.tagName.toLowerCase()),
        );
        const copy = child.cloneNode(true) as Element;
        for (const n of Array.from(copy.children)) {
          if (['ul', 'ol'].includes(n.tagName.toLowerCase())) copy.removeChild(n);
        }
        const line = inlineText(copy).replace(/\n+/g, ' ').trim();
        out += `- ${line}\n`;
        for (const n of nested) out += htmlNodeToPlain(n);
      }
      return out;
    }
    case 'li':
      return `- ${inlineText(el).replace(/\n+/g, ' ').trim()}\n`;
    default: {
      let out = '';
      for (const child of Array.from(el.childNodes)) {
        out += htmlNodeToPlain(child);
      }
      if (BLOCK_TAGS.has(tag) && out && !out.endsWith('\n')) out += '\n';
      return out;
    }
  }
}

export function journalHtmlToPlainText(html: string | null | undefined): string {
  const sanitized = sanitizeJournalHtml(html);
  if (!sanitized) return '';
  const doc = new DOMParser().parseFromString(`<div id="root">${sanitized}</div>`, 'text/html');
  const root = doc.getElementById('root') ?? doc.body;
  return collapseBlankLines(htmlNodeToPlain(root));
}

function jsonInline(node: JournalTipTapNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  return (node.content ?? []).map(jsonInline).join('');
}

export function journalJsonToPlainText(doc: JournalTipTapNode | null | undefined): string {
  if (!doc) return '';
  const walk = (node: JournalTipTapNode): string => {
    switch (node.type) {
      case 'doc':
        return (node.content ?? []).map(walk).join('');
      case 'paragraph':
        return paragraphPlain(jsonInline(node));
      case 'heading':
        return `${jsonInline(node).trim()}\n`;
      case 'bulletList':
      case 'orderedList':
        return (node.content ?? [])
          .map((item) => {
            const nested = (item.content ?? []).filter(
              (c) => c.type === 'bulletList' || c.type === 'orderedList',
            );
            const inlineNodes = (item.content ?? []).filter(
              (c) => c.type !== 'bulletList' && c.type !== 'orderedList',
            );
            const line = inlineNodes.map(jsonInline).join('').replace(/\n+/g, ' ').trim();
            return `- ${line}\n${nested.map(walk).join('')}`;
          })
          .join('');
      case 'listItem':
        return `- ${jsonInline(node).replace(/\n+/g, ' ').trim()}\n`;
      case 'text':
        return node.text ?? '';
      case 'hardBreak':
        return '\n';
      default:
        return (node.content ?? []).map(walk).join('');
    }
  };
  return collapseBlankLines(walk(doc));
}

/**
 * Convert stored formatted content (HTML string or TipTap JSON) to the
 * plain-text field consumers already read. Lists become '- ' lines.
 */
export function journalFormattedToPlainText(
  input: string | JournalTipTapNode | null | undefined,
): string {
  if (input == null) return '';
  if (typeof input !== 'string') return journalJsonToPlainText(input);
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && (parsed as JournalTipTapNode).type === 'doc') {
        return journalJsonToPlainText(parsed as JournalTipTapNode);
      }
    } catch {
      // Not JSON — treat as HTML or plain text.
    }
  }
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return journalHtmlToPlainText(input);
  }
  return collapseBlankLines(input.replace(/\r\n/g, '\n'));
}

export function isJournalHtmlEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  return journalHtmlToPlainText(html).length === 0;
}

export function storedJournalHtml(html: string | null | undefined): string | null {
  const sanitized = sanitizeJournalHtml(html);
  if (isJournalHtmlEmpty(sanitized)) return null;
  return sanitized;
}

/**
 * Whether the controlled html/text should call TipTap setContent.
 * Applies a new body (draft reopen, template insert/overwrite) even when
 * `html` matches `lastEmittedHtml`. Skips a parent echo of our last emit
 * when the editor already shows that body, and never replaces a non-empty
 * editor with an empty incoming value.
 */
export function shouldSetJournalEditorContent(opts: {
  incoming: string;
  current: string;
  html: string | null | undefined;
  text: string;
  lastEmittedHtml: string | null | undefined;
}): boolean {
  if (opts.incoming === opts.current) return false;
  const incomingBody = journalHtmlToPlainText(opts.incoming) || opts.text.trim();
  const currentBody = journalHtmlToPlainText(opts.current);
  if (incomingBody && incomingBody !== currentBody) return true;
  if (!incomingBody && currentBody) return false;
  if (opts.html === opts.lastEmittedHtml && (opts.html != null || !opts.text.trim())) {
    return false;
  }
  return true;
}
